import { useEffect, useMemo, useState } from 'react';
import { listMaklers } from '../services/maklerLevels.js';
import { listImports, createImport, createRow } from '../services/commission.js';

const norm = (s) =>
  String(s || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase();

// Accepts CSV/TSV text: makléř ; datum výplaty ; částka ; prodej/nájem.
function parseCsv(text, maklers) {
  const byName = new Map(maklers.map((m) => [norm(m.name), m]));
  return text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean)
    .map((line) => line.split(/[;\t]/).map((c) => c.trim()))
    .filter((c) => c.length >= 3 && !/^makl/i.test(c[0]))
    .map(([name, date, amount, type]) => {
      const m = byName.get(norm(name));
      const d = /^(\d{1,2})\.\s*(\d{1,2})\.\s*(\d{4})$/.exec(date || '');
      const iso = d ? `${d[3]}-${d[2].padStart(2, '0')}-${d[1].padStart(2, '0')}` : date;
      const amt = Number(String(amount).replace(/[\s.]/g, '').replace(',', '.'));
      let error = '';
      if (!m) error = 'Nesedí jméno makléře';
      else if (!iso || Number.isNaN(Date.parse(iso))) error = 'Neplatné datum';
      else if (!Number.isFinite(amt) || amt <= 0) error = 'Neplatná částka';
      return {
        name,
        maklerId: m?.id,
        commissionDate: iso,
        amount: amt,
        type: /n[aá]jem/i.test(type || '') ? 'Nájem' : 'Prodej',
        error,
      };
    });
}

const czk = (n) => `${Math.round(Number(n) || 0).toLocaleString('cs-CZ')} Kč`;

export default function CommissionImport() {
  const [maklers, setMaklers] = useState([]);
  const [batches, setBatches] = useState([]);
  const [rows, setRows] = useState([]);
  const [fileName, setFileName] = useState('');
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');
  const [manual, setManual] = useState({
    maklerId: '',
    commissionDate: new Date().toISOString().slice(0, 10),
    amount: '',
    type: 'Prodej',
  });

  const load = async () => {
    try {
      setMaklers(await listMaklers());
      setBatches(await listImports());
    } catch (e) {
      setError(e.message || 'Data se nepodařilo načíst.');
    }
  };

  useEffect(() => {
    load();
  }, []);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setMsg('');
    setError('');
    setFileName(file.name);
    setRows(parseCsv(await file.text(), maklers));
  };

  const stats = useMemo(() => {
    const ok = rows.filter((r) => !r.error);
    return {
      total: rows.length,
      sum: rows.reduce((s, r) => s + (r.amount || 0), 0),
      bad: rows.length - ok.length,
      ok,
    };
  }, [rows]);

  const save = async () => {
    setError('');
    try {
      const res = await createImport({
        commissionDate: new Date().toISOString().slice(0, 10),
        commissionRows: stats.ok.map((r) => ({
          maklerId: r.maklerId,
          commissionDate: r.commissionDate,
          amount: r.amount,
          type: r.type,
        })),
      });
      setMsg(`Dávka uložena: ${res.imported} řádků${res.failed ? `, ${res.failed} vyřazeno` : ''}. Tempo, kritéria a milníky se přepočítají.`);
      setRows([]);
      setFileName('');
      await load();
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Dávku se nepodařilo uložit.');
    }
  };

  const addManual = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await createRow({ ...manual, maklerId: Number(manual.maklerId) });
      setMsg('Provize přidána (původ „ručně“).');
      setManual({ ...manual, amount: '' });
      await load();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Provizi se nepodařilo přidat.');
    }
  };

  return (
    <section className="dashboard">
      <div className="dashboard__header">
        <div>
          <h1 className="dashboard__title">Nahrání vyplacených provizí</h1>
          <p className="dashboard__lead">
            Jednou týdně ve středu, dokud nebude napojení na Maxis – ve čtvrtek z nich jde
            report pro JCH. Z provizí se počítá tempo, kritéria úrovní a milníky.
          </p>
        </div>
      </div>

      {error && <p className="dashboard__error" role="alert">{error}</p>}
      {msg && <p className="dashboard__status" role="status">{msg}</p>}

      <div className="card">
        <h2 className="card__title">1. Nahraj soubor z Maxisu</h2>
        <p className="card__hint">
          CSV z Maxisu (oddělovač středník nebo tabulátor). Soubor musí obsahovat: makléř ·
          datum výplaty · částka · prodej / nájem.
        </p>
        <input type="file" accept=".csv,.txt,.tsv" onChange={onFile} />
        {fileName && <p className="card__text">Načteno: {fileName} · {rows.length} řádků</p>}
      </div>

      {rows.length > 0 && (
        <div className="card">
          <h2 className="card__title">2. Zkontroluj náhled</h2>
          <p className="card__text">
            Řádků {stats.total} · Součet {czk(stats.sum)} · K vyřazení {stats.bad} · Uloží se{' '}
            {stats.ok.length}
          </p>
          <div className="jr-table-wrap">
            <table className="jr-table">
              <thead>
                <tr><th>Výplata</th><th>Makléř</th><th>Částka</th><th>Typ</th><th>Kontrola</th></tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i}>
                    <td>{r.commissionDate}</td>
                    <td>{r.name}</td>
                    <td>{czk(r.amount)}</td>
                    <td>{r.type}</td>
                    <td>{r.error ? <span className="semafor semafor--red">{r.error}</span> : 'v pořádku'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <button className="button button--spot" type="button" disabled={!stats.ok.length} onClick={save}>
            Uložit dávku
          </button>
          <p className="card__hint">
            Vyřazené řádky nezmizí nadobro: oprav je v Maxisu nebo v souboru a nahraj znovu.
          </p>
        </div>
      )}

      <form className="card" onSubmit={addManual}>
        <h2 className="card__title">Zadat provizi ručně</h2>
        <p className="card__hint">Když v souboru něco chybí.</p>
        <div className="auth__row">
          <label className="auth__label">
            Makléř
            <select
              className="auth__input"
              required
              value={manual.maklerId}
              onChange={(e) => setManual({ ...manual, maklerId: e.target.value })}
            >
              <option value="">Vyber…</option>
              {maklers.map((m) => (
                <option key={m.id} value={m.id}>{m.name}</option>
              ))}
            </select>
          </label>
          <label className="auth__label">
            Datum výplaty
            <input
              className="auth__input"
              type="date"
              required
              value={manual.commissionDate}
              onChange={(e) => setManual({ ...manual, commissionDate: e.target.value })}
            />
          </label>
          <label className="auth__label">
            Částka (Kč)
            <input
              className="auth__input"
              required
              inputMode="decimal"
              value={manual.amount}
              onChange={(e) => setManual({ ...manual, amount: e.target.value })}
            />
          </label>
          <label className="auth__label">
            Typ
            <select
              className="auth__input"
              value={manual.type}
              onChange={(e) => setManual({ ...manual, type: e.target.value })}
            >
              <option>Prodej</option>
              <option>Nájem</option>
            </select>
          </label>
        </div>
        <button className="button button--spot" type="submit">Přidat provizi</button>
      </form>

      <div className="card">
        <h2 className="card__title">Dávky</h2>
        <ul className="jr-list">
          {batches.map((b) => (
            <li key={b.id}>
              {new Date(b.batch_date).toLocaleDateString('cs-CZ')} · {b.status}
            </li>
          ))}
          {batches.length === 0 && <li>Zatím žádné dávky.</li>}
        </ul>
      </div>
    </section>
  );
}
